import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  LegacyRef,
} from "react";
import { useTranslation } from "react-i18next";

// API
import { getAlertById } from "../../../setup/api/services/alerts";

// Types
import { IAlert } from "../../../setup/api/services/alerts/models";

// Components
import AlertWidget from "../../common/AlertWidget/AlertWidget";

// Styles
import styles from "./TestAlertModal.module.scss";
import PrimaryButton, {
  PrimaryButtonSize,
  PrimaryButtonType,
} from "../../common/PrimaryButton";

// ============================================================================
// INTERFACES
// ============================================================================

export interface TestAlertModalRef {
  openModal: (alertId: string) => void;
  closeModal: () => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

const TestAlertModal = forwardRef<TestAlertModalRef>((_, ref) => {
  const { t } = useTranslation();

  // ========================================================================
  // STATE
  // ========================================================================

  const [open, setOpen] = useState<boolean>(false);
  const [alertId, setAlertId] = useState<string | null>(null);
  const [alert, setAlert] = useState<IAlert | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // ========================================================================
  // HANDLERS
  // ========================================================================

  const closeModal = useCallback(() => {
    setOpen(false);
    setAlertId(null);
    setAlert(null);
    setError(null);
  }, []);

  const openModal = useCallback(
    async (id: string) => {
      setOpen(true);
      setAlertId(id);
      setLoading(true);
      setError(null);

      try {
        const alertData = await getAlertById(id);
        setAlert(alertData);
      } catch (err) {
        console.error("Failed to load alert:", err);
        setError(t("alert_fetch_error"));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  const stopPropagation = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  // ========================================================================
  // IMPERATIVE HANDLE
  // ========================================================================

  useImperativeHandle(ref, () => ({
    openModal,
    closeModal,
  }));

  // ========================================================================
  // RENDER
  // ========================================================================

  if (!open) return null;

  return (
    <div ref={ref as LegacyRef<HTMLDivElement>} className={styles.modal}>
      {/* Backdrop */}
      <div onClick={closeModal} className={styles.mask} />

      {/* Modal Content */}
      <div onClick={stopPropagation} className={styles.content}>
        {/* Header with Close Button */}
        <div className={styles.header}>
          <h2 className={styles.title}>{t("alert_preview_alert")}</h2>
          <div onClick={closeModal} className={styles.close_button}>
            <img src="/assets/images/svg/cross_icon.svg" alt="close" />
          </div>
        </div>

        {/* Alert Preview */}
        <div className={styles.preview_container}>
          {loading && <div className={styles.loading}>{t("lbl_loading")}</div>}

          {error && <div className={styles.error}>{error}</div>}

          {alert && !loading && !error && (
            <AlertWidget
              activeAlertWidget={{
                id: parseInt(alert.id),
                cta: {
                  enabled: alert.cta.enabled,
                  url: alert.cta.url || "",
                  label: alert.cta.label || "",
                },
                severity: alert.severity,
                createdAt: alert.createdAt,
                updatedAt: alert.updatedAt,
                messageAr: alert.messageAr,
                surfaces: alert.surfaces,
                status: alert.status,
              }}
            />
          )}
        </div>

        {/* Footer with Back Button */}
        <div className={styles.footer}>
          <PrimaryButton
            onClick={closeModal}
            buttonType={PrimaryButtonType.PRIMARY_LIGHT}>
            <p className={styles.button_text}>{t("closed")}</p>
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
});

TestAlertModal.displayName = "TestAlertModal";

export default TestAlertModal;
